import { IGovernancePromptMonitoringStatsFilters, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { useEffect, useState } from 'react';
import { promptMonitoringEntityDomainsRequest } from './promptMonitoringStatsRequests';
import { scheduleStatsRequest, StatsRequestPriority } from './statsRequestQueue';

/**
 * The domain that represents each tracked entity, for putting a favicon beside its name and for
 * offering an inferred domain on the tag form.
 *
 * Sync Core resolves this from an operator-curated domain where one exists, and otherwise infers it
 * from the hosts cited alongside the entity across runs. The inference is guarded hard and
 * **abstains far more often than it answers** — an entity with thin or contradictory evidence is
 * simply absent from the response.
 *
 * That shape is the whole contract here: a missing entry means "unknown", never "use something
 * else". A wrong domain puts a competitor's favicon beside a brand, which is worse than no favicon
 * at all, so there is deliberately no fallback anywhere in this file.
 */

/**
 * A resolved domain for one entity, with where it came from and how much evidence stands behind it.
 *
 * `source` says whether an operator curated the domain or the service inferred it; the tag form
 * offers only an inferred one, since a curated domain is already the stored value. `distinctRuns`
 * is what the suggestion shows as its run count.
 */
export interface EntityDomainSuggestion {
  domain: string;
  source: 'curated' | 'inferred';
  weightedShare?: number;
  distinctRuns?: number;
}

/** Resolved domains, keyed by taxonomy term key. Absent key = we do not know. */
export type EntityDomainMap = ReadonlyMap<string, EntityDomainSuggestion>;

const EMPTY: EntityDomainMap = new Map();

/**
 * Loads the entity→domain map, optionally scoped to one prompt purpose.
 *
 * A purpose narrows the evidence to that purpose's runs. Without one the hook abstains — the
 * favicon callers pass a purpose and resolve nothing until one is chosen — unless the caller sets
 * `includeProjectWide`, the explicit opt-in the tag form uses to infer across the whole project.
 * The endpoint treats the purpose as optional even though the shared filter type marks it required,
 * so a project-wide call passes an empty filter at that boundary.
 *
 * Never throws into the render path: a failed lookup leaves the map empty and the names render
 * exactly as they did before favicons existed.
 *
 * `queuePriority` sends the lookup through the statistics queue, for a caller on the AI Visibility
 * screen whose other sections share it. Every other caller asks for this one lookup directly, so
 * it never waits behind requests a screen the reader has left still has at Sync Core. In the queue
 * the lookup is silent: it says nothing when it fails and offers no Try again, so a combined request
 * Sync Core does not answer fails a section that does, and the lookup is sent again after the pause.
 */
export function useEntityDomains(
  api: ISyncCoreApi | undefined,
  purposeTermKey?: string,
  options?: { includeProjectWide?: boolean; queuePriority?: StatsRequestPriority }
): EntityDomainMap {
  const [domains, setDomains] = useState<EntityDomainMap>(EMPTY);
  const includeProjectWide = options?.includeProjectWide ?? false;
  const queuePriority = options?.queuePriority;

  useEffect(() => {
    const stats = api?.governance?.promptMonitoringStats;
    if (!stats?.entityDomains) {
      // No api, or a service too old to answer: abstain, exactly as an empty response would.
      setDomains(EMPTY);
      return;
    }

    const purpose = purposeTermKey?.trim();
    if (!purpose && !includeProjectWide) {
      // No purpose and no opt-in: abstain rather than fire a project-wide request the caller did
      // not ask for — the favicon callers show nothing until a purpose is chosen.
      setDomains(EMPTY);
      return;
    }

    // The endpoint's `purposeTermKey` is optional; the shared filter type over-constrains it as
    // required, so an empty project-wide filter is passed through this one documented boundary.
    const filters: IGovernancePromptMonitoringStatsFilters = purpose
      ? { purposeTermKey: purpose }
      : ({} as IGovernancePromptMonitoringStatsFilters);

    let active = true;
    // Aborted when the screen is left or the purpose changes, so a queued lookup nobody reads any
    // more never holds up the next one.
    const superseded = new AbortController();
    const lookup =
      queuePriority === undefined
        ? stats.entityDomains(filters)
        : scheduleStatsRequest({
            ...promptMonitoringEntityDomainsRequest({ governance: { promptMonitoringStats: stats } }, filters),
            priority: queuePriority,
            signal: superseded.signal,
            silent: true,
          });
    void lookup
      .then((response) => {
        if (!active) {
          return;
        }
        const next = new Map<string, EntityDomainSuggestion>();
        for (const row of response?.rows || []) {
          const termKey = String(row?.termKey || '').trim();
          const domain = String(row?.domain || '').trim();
          // Both halves have to be present for the row to mean anything.
          if (!termKey || !domain) {
            continue;
          }
          next.set(termKey, {
            domain,
            source: row?.source === 'curated' ? 'curated' : 'inferred',
            ...(typeof row?.weightedShare === 'number' ? { weightedShare: row.weightedShare } : {}),
            ...(typeof row?.distinctRuns === 'number' ? { distinctRuns: row.distinctRuns } : {}),
          });
        }
        setDomains(next);
      })
      .catch(() => {
        if (active) {
          // A failed lookup is indistinguishable from "nothing resolved" as far as the reader is
          // concerned, and both correctly produce no favicon.
          setDomains(EMPTY);
        }
      });

    return () => {
      active = false;
      superseded.abort();
    };
  }, [api, purposeTermKey, includeProjectWide, queuePriority]);

  return domains;
}
